import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Chip, Button } from '@mui/material';
import {
  Rocket, Visibility, Favorite, Shield, Speed, Groups,
  ArrowForward, CheckCircle, Email, Domain, Security, Cloud,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const values = [
  { icon: <Shield sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Privacy First', desc: 'Your data belongs to you. We never scan emails for ads, never sell data, and provide full encryption at rest and in transit.' },
  { icon: <Speed sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Performance', desc: 'We obsess over speed. From sub-second search to instant delivery, every millisecond matters to us.' },
  { icon: <Visibility sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Transparency', desc: 'Clear pricing, honest communication, and an open roadmap. No surprises, no hidden fees, no vendor lock-in.' },
  { icon: <Favorite sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Customer Focused', desc: 'Every feature we build starts with customer feedback. Our support team treats your problems as our own.' },
  { icon: <Rocket sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Innovation', desc: 'From AI-powered composition to smart filtering, we bring cutting-edge technology to business email.' },
  { icon: <Groups sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Reliability', desc: 'Built on enterprise-grade infrastructure with 99.99% uptime, automated failover, and 24/7 monitoring.' },
];

const stats = [
  { value: '99.99%', label: 'Uptime SLA' },
  { value: '50M+', label: 'Emails Delivered' },
  { value: '10K+', label: 'Active Mailboxes' },
  { value: '24/7', label: 'Expert Support' },
];

const milestones = [
  { year: '2023', title: 'Founded', desc: 'CruvzMail was founded with a mission to build a private, enterprise-grade email platform that businesses actually own.' },
  { year: '2024', title: 'Platform Launch', desc: 'Launched the full webmail suite — inbox, compose, calendar, contacts, tasks, and the admin panel — for early customers.' },
  { year: '2025', title: 'AI & Collaboration', desc: 'Added AI-powered email composition, DriveX file storage, OnlyOffice integration, and team helpdesk module.' },
  { year: '2026', title: 'Scale & Security', desc: 'Expanded to a multi-VM clustered infrastructure with HAProxy load balancing, automated SSL, and DMARC enforcement.' },
];

const pillars = [
  { icon: <Email sx={{ fontSize: 24, color: '#dc2626' }} />, title: 'Email That You Own', desc: 'No ads, no scanning, no third-party access. Your communications stay between you and your recipients.' },
  { icon: <Domain sx={{ fontSize: 24, color: '#dc2626' }} />, title: 'Your Domain, Your Brand', desc: 'Custom domains, white-label branding, and per-domain DKIM/SPF/DMARC — everything under your identity.' },
  { icon: <Security sx={{ fontSize: 24, color: '#dc2626' }} />, title: 'Enterprise Security', desc: 'Two-factor authentication, IP restrictions, session management, anti-spam, and encrypted storage by default.' },
  { icon: <Cloud sx={{ fontSize: 24, color: '#dc2626' }} />, title: 'Cloud Infrastructure', desc: 'Hosted on high-availability cloud infrastructure with automatic failover, backups, and 24/7 monitoring.' },
];

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -150, right: -150, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 820, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Our Story" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.8rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Built for Businesses That Value Privacy
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 620, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            CruvzMail is a professional email platform delivering enterprise-grade communication, security, and collaboration — with no ads, no data mining, and complete privacy.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{
              px: 4, py: 1.5, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
              bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' },
            }}>Get Started Free</Button>
            <Button onClick={() => navigate('/contact')} sx={{
              px: 4, py: 1.5, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
              color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.4)' },
            }}>Contact Us</Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: { xs: 5, md: 6 }, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 4 }}>
          {stats.map((s) => (
            <Box key={s.label} sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 900, color: '#f87171', lineHeight: 1 }}>{s.value}</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', fontWeight: 500, mt: 0.5 }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── MISSION ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 8, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Our Mission</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.2rem' }, lineHeight: 1.2, mb: 3 }}>
              Email Infrastructure Built for the Long Game
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#475569', lineHeight: 1.85, mb: 3 }}>
              We believe every business deserves email infrastructure they truly own — not rented from a big-tech ad network. CruvzMail gives you the power and privacy of running your own mail server, without the operational burden.
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#475569', lineHeight: 1.85, mb: 4 }}>
              From a growing startup to a multi-domain enterprise, CruvzMail scales with you. Our platform handles SMTP delivery, IMAP sync, anti-spam, DKIM signing, admin controls, and collaboration tools — all in one place, always online.
            </Typography>
            {['No ads, no email scanning', 'Full SPF / DKIM / DMARC control', 'Multi-domain with white-label branding', 'Enterprise security by default'].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
                <CheckCircle sx={{ fontSize: 18, color: '#16a34a', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.9rem', color: '#0f172a', fontWeight: 500 }}>{item}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              {pillars.map((p) => (
                <Grid size={{ xs: 12, sm: 6 }} key={p.title}>
                  <Box sx={{
                    p: 3, borderRadius: 3, bgcolor: '#fff', border: '1px solid #e2e8f0', height: '100%',
                    transition: 'all 0.2s', '&:hover': { borderColor: '#fecaca', boxShadow: '0 4px 20px rgba(220,38,38,0.06)' },
                  }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fef2f2', display: 'inline-flex', mb: 1.5 }}>{p.icon}</Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', mb: 0.5 }}>{p.title}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.65 }}>{p.desc}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* ── TIMELINE ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Journey</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' } }}>How We Got Here</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {milestones.map((m, i) => (
              <Box key={m.year} sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', pb: 5, position: 'relative' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 80 }}>
                  <Box sx={{ bgcolor: '#dc2626', color: '#fff', px: 2, py: 0.8, borderRadius: 2, fontSize: '0.9rem', fontWeight: 800 }}>{m.year}</Box>
                  {i < milestones.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: '#f1f5f9', mt: 1.5, minHeight: 40 }} />}
                </Box>
                <Box sx={{ flex: 1, pt: 0.3 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a', mb: 0.5 }}>{m.title}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.75 }}>{m.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── VALUES ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Core Values</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' } }}>What We Stand For</Typography>
          </Box>
          <Grid container spacing={3}>
            {values.map((v) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={v.title}>
                <Card elevation={0} sx={{
                  height: '100%', borderRadius: 3, border: '1px solid #e2e8f0',
                  transition: 'all 0.25s', '&:hover': { borderColor: '#fecaca', transform: 'translateY(-3px)', boxShadow: '0 8px 30px rgba(220,38,38,0.06)' },
                }}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: '#fef2f2', display: 'inline-flex', mb: 2 }}>{v.icon}</Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', mb: 1 }}>{v.title}</Typography>
                    <Typography sx={{ fontSize: '0.86rem', color: '#64748b', lineHeight: 1.75 }}>{v.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{
        py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{
            fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.6rem' }, mb: 2,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>Ready to Own Your Email?</Typography>
          <Typography sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.6)', mb: 5, maxWidth: 480, mx: 'auto', lineHeight: 1.8 }}>
            Join businesses that chose privacy, reliability, and a professional email experience.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{
              px: 4, py: 1.5, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
              bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' },
            }}>Start Free Trial</Button>
            <Button onClick={() => navigate('/pricing')} sx={{
              px: 4, py: 1.5, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
              color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            }}>View Pricing</Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default AboutPage;
